import { supabase } from "@/integrations/supabase/client";
import type { FamilyBalance, FamilyGroup, FamilyMember, FamilyPendingInvite, FamilyRole } from "./types";

export async function listMyFamilyGroups(): Promise<FamilyGroup[]> {
  const { data, error } = await supabase
    .from("family_groups")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as FamilyGroup[];
}

export async function createFamilyGroup(name: string): Promise<FamilyGroup> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new Error("not authenticated");
  const { data, error } = await supabase
    .from("family_groups")
    .insert({ name, owner_id: u.user.id })
    .select()
    .single();
  if (error) throw error;
  return data as FamilyGroup;
}

export async function listFamilyMembers(groupId: string): Promise<FamilyMember[]> {
  const { data, error } = await supabase
    .from("family_members")
    .select("*")
    .eq("group_id", groupId)
    .order("joined_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as FamilyMember[];
}

export async function joinFamilyByCode(code: string): Promise<string> {
  const { data, error } = await supabase.rpc("family_join_by_code", { _code: code });
  if (error) throw error;
  return data as string;
}

export async function getFamilyBalance(groupId: string): Promise<FamilyBalance | null> {
  const { data, error } = await supabase.rpc("family_group_balance", { _group_id: groupId });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return (row as FamilyBalance) ?? null;
}

export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase.from("family_members").delete().eq("id", memberId);
  if (error) throw error;
}

export async function inviteByCashtag(groupId: string, cashtag: string, role: FamilyRole = "member"): Promise<string> {
  const { data, error } = await supabase.rpc("family_invite_user", {
    _group_id: groupId,
    _cashtag: cashtag,
    _role: role,
  });
  if (error) throw error;
  return data as string;
}

export async function listMyPendingInvites(): Promise<FamilyPendingInvite[]> {
  const { data, error } = await supabase.rpc("family_my_pending_invites");
  if (error) throw error;
  return (data ?? []) as FamilyPendingInvite[];
}

export async function acceptInvite(groupId: string): Promise<void> {
  const { error } = await supabase.rpc("family_accept_invite", { _group_id: groupId });
  if (error) throw error;
}

export async function declineInvite(groupId: string): Promise<void> {
  const { error } = await supabase.rpc("family_decline_invite", { _group_id: groupId });
  if (error) throw error;
}
